import * as React from "react"
import { Search, X } from "lucide-react"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

/**
 * Barra de filtros. Es cromo, no contenido: fondo hundido, sin sombra y
 * separada de la tabla por un filete. Los controles se alinean por su linea
 * base para que una fila de selects y fechas lea como una sola banda.
 */
export function Toolbar({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-guia="filtros"
      className={cn(
        "flex flex-wrap items-end gap-x-3 gap-y-2 rounded-md border bg-surface-sunken px-2.5 py-2",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

interface ToolbarFieldProps {
  label: string
  htmlFor?: string
  className?: string
  children: React.ReactNode
}

export function ToolbarField({ label, htmlFor, className, children }: ToolbarFieldProps) {
  return (
    <div className={cn("space-y-1", className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}

/** Nota al pie de la barra: explica el comportamiento, no adorna. */
export function ToolbarNote({
  icon: Icon,
  children,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>
  children: React.ReactNode
  className?: string
}) {
  return (
    <p
      className={cn(
        "flex max-w-[52ch] items-start gap-1.5 pb-1 text-2xs leading-4 text-muted-foreground",
        className,
      )}
    >
      {Icon ? <Icon className="mt-px h-3 w-3 shrink-0" /> : null}
      <span>{children}</span>
    </p>
  )
}

interface SearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  value: string
  onValueChange: (value: string) => void
  containerClassName?: string
}

/** Busqueda con lupa y borrado en un gesto: en una cola se filtra a cada rato. */
export function SearchInput({
  value,
  onValueChange,
  className,
  containerClassName,
  ...props
}: SearchInputProps) {
  return (
    <div className={cn("relative", containerClassName)}>
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        className={cn("pl-7 pr-7 [&::-webkit-search-cancel-button]:hidden", className)}
        {...props}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onValueChange("")}
          aria-label={t("Limpiar busqueda")}
          className="absolute right-1 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  )
}
